import defaultSettings  from './BinderSettings';
import Utilities        from '../../utilities';
import {
  _appendDataBindAttr,
  _makeObservable,
  _mergeData
}                       from './BinderMethods';
import {
  camelCase as _camelCase,
}                       from 'lodash';
import 'mdn-polyfills/Object.values';
import 'mdn-polyfills/Array.from';
import 'mdn-polyfills/Array.prototype.includes';
import 'mdn-polyfills/String.prototype.includes';
import 'mdn-polyfills/Object.entries';
import 'mutationobserver-shim';
import { numberFormat } from '../../../formatters/numberFormat';

class BinderManager {
  /**
   *
   * @param {HTMLElement} target
   * @param {Object} [existingViewModel]
   * @param {Boolean} [makeObservale] - Rende observable i dati dell'existingModel
   * @param {Object} [koInstance]
   */
  constructor (target, existingViewModel, makeObservale, koInstance) {
    /**
     * @type {HTMLElement}
     */
    this.target = target;
    
    /**
     * @type {Object}
     */
    this.settings = defaultSettings;
    
    if (existingViewModel && makeObservale) {
      existingViewModel = _makeObservable(existingViewModel);
    }
    
    /**
     * @type {{}}
     */
    this.observablesList = existingViewModel || {};
    
    /**
     * @type {{}}
     */
    this.subscribersList = {};
    
    this.koInstance = koInstance;
    
    this._mapBindableElements();
    
    if (!window.ko) {
      window.ko = koInstance;
    }
    
    this._createExtenders();
    this._searchForCustomViewModels();
    
    this.observablesList.toObject = () => {
      return this.koInstance.toJS(this.observablesList);
    };
    
    this.koInstance.applyBindings(this.observablesList, (target instanceof Document) ? target.body : target);
  }
  
  _createExtenders () {
    this.koInstance.extenders.numberFormat = (target, precision) => {
      const result = this.koInstance.pureComputed({
        read:  () => numberFormat(target()),
        write: (newValue) => {
          if (this.koInstance.isWritableObservable(target)) {
            target(newValue);
          }
        }
      });
      
      result(target);
      
      return result;
    };
    
  }
  
  /**
   * Ricerca tutti gli elementi con attributo "fw-bind-to".
   * Per ciascuno di loro, ne converte gli attributi in parametri validi per Knockout.js
   *
   * @private
   */
  _mapBindableElements () {
    /**
     * @type {NodeList}
     */
    const observableElements = this.target.querySelectorAll(this._getPrefixSelector());
    
    // Effettua un primo loop sugli elementi html che sono Osservabili aggiungendo il binding degli attributi
    // relativi al valore effettivo dei componenti, come value, checked.
    observableElements.forEach(
      /**
       * @param {HTMLElement} element
       */
      element => {
        const params       = this._getElementParams(element);
        const name         = params.to;
        /**
         *
         * @type {NodeList}
         */
        const htmlElements = this._getObservableHTMLElement(name);
        
        htmlElements.forEach(
          /**
           * @param {HTMLElement|HTMLInputElement} observableElement
           */
          observableElement => {
            const elType = observableElement.type;
            
            switch (elType) {
            case 'radio': {
              const val = observableElement.checked ? observableElement.value : '';
              
              if (!this.observablesList[name] || val) {
                this.observablesList[name] = this.koInstance.observable(val);
              }
              
              _appendDataBindAttr(observableElement, 'checked: ' + name, true);
              
              break;
            }
            case 'checkbox': {
              this.observablesList[name] = this.koInstance.observable(observableElement.checked);
              _appendDataBindAttr(observableElement, 'checked: ' + name, true);
              
              break;
            }
            default:
              let field = 'value';
              
              // Se l'elemento non è un input, textarea o select, setta il valore "text" e recupera il contenuto
              // tramite innerText.
              if (!['input', 'textarea', 'select'].includes(observableElement.localName.toString().toLowerCase())) {
                field = 'text';
              }
              
              let value   = observableElement[field === 'value' ? field : 'innerText'];
              let newName = name;
              
              /**
               * Se viene settato il parametro fw-bind-type, questo viene passato direttamente a Ko.
               */
              if (params.type === 'observableArray') {
                newName = `JSON.stringify(${name}())`;
                
                try {
                  value = JSON.parse(value);
                } catch (e) {
                  value = [];
                }
              }
              
              this.observablesList[name] = this.koInstance[params.type || 'observable'](value);
              _appendDataBindAttr(observableElement, field + ': ' + newName, true);
              
              break;
            }
          }
        );
      }
    );
    
    // Effettua un secondo loop sugli elementi html che Osservano gli Osservabili, aggiungendone i paramentri necessari
    // per il binding tramite Knockout.j
    observableElements.forEach(
      /**
       * @param {HTMLElement} element
       */
      element => {
        const params     = this._getElementParams(element);
        const targetName = params.to;
        
        if (!this.subscribersList[targetName]) {
          this.subscribersList[targetName] = [];
        }
        
        this.subscribersList[targetName].push(element);
        
        // Controlla ed aggiunge il mutationObserver sugli attributi dell'elemento qualora sia il caso
        this._addAttributesObserver(element);
        
        // Per ogni elemento, ne aggiunge l'attributo data-bind in ascolto su una computed property;
        this._addElementBinding(element, params);
      }
    );
  }
  
  /**
   * Dato un elemento, ne elabora gli attributi "fw-bind" e in base ad essi
   * ne aggiunge gli attributi necessari a Knockout.js.
   *
   * @param {HTMLElement} element
   * @param {ControlsBindingNS.ElementAttributes} params
   * @private
   */
  _addElementBinding (element, params) {
    let conditionValue = params.actionConditionValue || params.actionValue;
    const positive       = ['show', 'enable', 'select', 'required'].includes(params.action);
    let condition        = params.to + '()';
    
    // Setta la condizione se positiva o negativa..
    if (typeof conditionValue !== 'undefined') {
      // Se il valore contiene una condizione || o && la mantiene.
      if (conditionValue.indexOf('||') > -1 || conditionValue.indexOf('&&') > -1) {
        const groups           = conditionValue.split(/\|\||&&/);
        const groupsConditions = conditionValue.match(/\|\||&&/g);
        const subCondition     = [];
        
        groups.forEach((str, index) => {
          subCondition.push(`${condition} === '${str.trim()}'`);
          
          if (groupsConditions[index]) {
            subCondition.push(groupsConditions[index]);
          }
        });
        
        // Compongo la condizione. Nel caso in cui l'action sia hide o disable inverto la condizione: !( condition )
        condition = `${!positive ? '!(' : ''} ${subCondition.join(' ')} ${!positive ? ')' : ''}`;
      } else {
        if(conditionValue.includes("'"))
        {
          conditionValue = JSON.stringify(conditionValue).replace(/\'/g, "");
        }
        condition += ` ${positive ? '=' : '!'}== '${conditionValue}'`;
      }
    } else {
      condition = `${positive ? '' : '!'}${condition}`;
    }

    condition = `(${condition})`;
    
    switch (params.action) {
      case 'hide':
      case 'show': {
        if (element.classList.contains('collapse')) {
          _appendDataBindAttr(element, `css: { in: ${condition}, show: ${condition} }`);
        } else {
          _appendDataBindAttr(element, 'visible: ' + condition);
        }

        // Disabilita gli elementi che vengono nascosti
        if (this.settings.disableableElements.includes(element.localName)) {
          _appendDataBindAttr(element, 'enable: ' + condition);
        } else {
          element.querySelectorAll(this.settings.disableableElements.join(',')).forEach(subElement => {
            // Controlla ed aggiunge il mutationObserver sugli attributi dell'elemento qualora sia il caso
            this._addAttributesObserver(subElement);

            _appendDataBindAttr(subElement, 'enable: ' + condition);
          });
        }

        break;
      }

      case 'enable':
      case 'disable': {
        if (this.settings.requireableElements.includes(element.localName)) {
          _appendDataBindAttr(element, 'enable: ' + condition);
        } else {
          element.querySelectorAll(this.settings.requireableElements.join(',')).forEach(subElement => {
            _appendDataBindAttr(subElement, 'enable: ' + condition);
          });
        }

        break;
      }
    
      case 'select':
      case 'deselect': {
        if (element.type === 'checkbox') {
          _appendDataBindAttr(element, 'checked: ' + condition);
        } else {
          element.querySelectorAll('input[type="checkbox"]').forEach(subElement => {
            _appendDataBindAttr(subElement, 'checked: ' + condition);
          });
        }

        break;
      }

      case 'required': {
        if (this.settings.requireableElements.includes(element.localName)) {
          _appendDataBindAttr(element, `attr:{regola: ${condition} ? 'required':''}`);
        } else {
          element.querySelectorAll(this.settings.requireableElements.join(',')).forEach(subElement => {
            _appendDataBindAttr(element, `attr:{regola: ${condition} ? 'required':''}`);
          });
        }
        break;
      }
    }
  }
  
  /**
   * Aggiunge un Observer sugli attributi dell'element speficiato.
   * Questo per triggerare eventuali eventi relative ad altre librerie che potrebbero essere
   * attive sull'elemento.
   *
   * Attualmente questo viene usato per l'attributo "disabled" in modo che al cambio sugli elementi HTMLSelectElement,
   * se c'è attiva la libreria Selectpicker, triggera il refresh in modo che l'aspetto del componente venga aggiornato
   * correttamente in base all'attributo cambiato.
   *
   * @param {HTMLElement} element
   * @param {boolean} element.mObserverAdded
   * @private
   */
  _addAttributesObserver (element) {
    const classesToSearchFor = ['selectpicker'];
    let classesAreValid      = false;
    
    element.classList.forEach(className => {
      if (classesToSearchFor.includes(className)) {
        classesAreValid = true;
      }
    });
    
    // per gli elementi select, visto che possono avere delle librerie esterne in ascolto come selectpicker,
    // Occorre restare in ascolto al cambio degli attributi, come disabled, in modo da triggerare il refresh del componente.
    if (!classesAreValid || element.localName !== 'select' || element.mObserverAdded) {
      return;
    }
    
    // Opzioni per il monitoraggio (quali mutazioni monitorare)
    const config = {
      attributes: true,
      childList:  false,
      subtree:    false
    };
    
    // Funzione di callback da eseguire quando avvengono le mutazioni
    const callback = function (mutationsList) {
      for (const mutation of mutationsList) {
        if (mutation.type === 'attributes') {
          // Attributo "DISABLED"
          if (mutation.attributeName === 'disabled') {
            const target  = mutation.target;
            const $target = $(target);
            
            // Caso in cui ci sia la libreria "Selectpicker"
            if ($target.data('selectpicker')) {
              $target.selectpicker('refresh');
            }
          }
        }
      }
    };
    
    // Creazione di un'istanza di monitoraggio collegata alla funzione di callback
    const observer = new MutationObserver(callback);
    
    element.mObserverAdded = true;
    
    observer.observe(element, config);
  }
  
  /**
   * Ritorna i parametri presenti come attributi su di un elemento, che iniziano col prefisso "fw-control-bind"
   *
   * @param {HTMLElement} element
   * @return {ControlsBindingNS.ElementAttributes}
   * @private
   */
  _getElementParams (element) {
    const attributes = Object.values(element.attributes);
    
    return attributes.reduce(
      (acc, curr) => {
        if (curr && curr.name && this._nameContainsPrefix(curr.name)) {
          const propName = _camelCase(curr.name.replace(new RegExp('(' + defaultSettings.prefix.join('|') + ')-', 'g'), ''));
          
          acc[propName] = curr.value;
        }
        
        return acc;
      },
      /**
       * @type {ControlsBindingNS.ElementAttributes}
       */
      {}
    );
  }
  
  /**
   * Dato il NAME dell'elemento, ne ritorna l'HTMLElement
   *
   * @returns {NodeList}
   * @private
   */
  _getObservableHTMLElement (name) {
    return this.target.querySelectorAll(`[name='${name}']`);
  }
  
  _getPrefixSelector () {
    const selector = this.settings.prefix.reduce(function (acc, curr) {
      acc.push(`[${curr}-to]`);
      
      return acc;
    }, []);
    
    return selector.join(',');
  }
  
  /**
   *
   * @param {string} name
   * @private
   */
  _nameContainsPrefix (name) {
    let result = false;
    
    defaultSettings.prefix.forEach(function (prefix) {
      if (name.includes(prefix)) {
        result = true;
      }
    });
    
    return result;
  }
  
  _searchForCustomViewModels () {
    const foundedScripts = (Utilities.get_js_target(this.target)).querySelectorAll('script[type=\'text/jsx\']');
    
    if (!foundedScripts || foundedScripts.length === 0) {
      return;
    }
    
    for (const script of foundedScripts) {
      const content = script.textContent;
      let evaluatedContent;
      let evaluatedContentKeys;
      
      try {
        this.makeObservable = _makeObservable;
        
        // eslint-disable-next-line no-eval
        evaluatedContent     = eval(content);
        evaluatedContentKeys = Object.keys(evaluatedContent);
      } catch (evalError) {
        console.error(evalError, script);
        continue;
      }
      
      evaluatedContent = _makeObservable(evaluatedContent);
      
      for (const key of evaluatedContentKeys) {
        if (!this.observablesList[key]) {
          let value = evaluatedContent[key];
          
          this.observablesList[key] = value;
        }
      }
    }
  }
  
  refreshBinding (viewModel, target) {
    this.koInstance.cleanNode(target);
    this.koInstance.applyBindingsToNode(target, viewModel);
  }
  
  destroyInstance (removeDataBind = true) {
    const target = (this.target instanceof Document) ? this.target.body : this.target;
    
    this.koInstance.cleanNode(target);
    
    if (removeDataBind) {
      target.querySelectorAll('[data-bind]').forEach(function (el) {
        const originalDataBind = el.originalDataBind;
        
        if (originalDataBind) {
          el.setAttribute('data-bind', originalDataBind);
        } else {
          el.removeAttribute('data-bind');
        }
      });
    }
  }
}

export { BinderManager, _mergeData };
